// Shared CKEditor setup so every editor in the app behaves the same.
//
// We use the new "ckeditor5" package installation method (not the old
// predefined `ckeditor5-build-classic` build) because the predefined build
// does NOT ship the Alignment plugin and does not allow adding plugins.
// Here we import ClassicEditor plus exactly the plugins we need, so the
// alignment buttons (left / center / right / justify) actually load.
//
// `heading.options` maps each model heading to the matching view tag
// (h1->h1, h2->h2, ...). By default CKEditor shifts them one level up on
// paste (a Word H2 becomes H1); this mapping keeps the exact heading levels.

import {
  ClassicEditor,
  Essentials,
  Autoformat,
  Bold,
  Italic,
  BlockQuote,
  Heading,
  Alignment,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  Base64UploadAdapter,
  Indent,
  IndentBlock,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TextTransformation,
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

const ckeditorConfig = {
  licenseKey: 'GPL',
  plugins: [
    Essentials,
    Autoformat,
    Bold,
    Italic,
    BlockQuote,
    Heading,
    Alignment,
    Image,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageResize,
    Base64UploadAdapter,
    Indent,
    IndentBlock,
    Link,
    List,
    MediaEmbed,
    Paragraph,
    PasteFromOffice,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TextTransformation,
  ],
  toolbar: {
    items: [
      'undo', 'redo', '|',
      'heading', '|',
      'bold', 'italic', '|',
      'alignment', '|',
      'link', 'blockQuote', '|',
      'bulletedList', 'numberedList', '|',
      'outdent', 'indent', '|',
      'insertTable', '|',
      'uploadImage', 'mediaEmbed',
    ],
    shouldNotGroupWhenFull: true,
  },
  alignment: {
    options: [ 'left', 'center', 'right', 'justify' ],
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
      { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
    ],
  },
  image: {
    toolbar: [
      'imageStyle:inline',
      'imageStyle:block',
      'imageStyle:side',
      '|',
      'toggleImageCaption',
      'imageTextAlternative',
    ],
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
    ],
  },
};

// When you paste from Word / Google Docs, CKEditor keeps ALL of the source
// formatting: junk <div>/<span> wrappers, `class` attributes, and inline
// `style` on almost every element (tables even get a <figure class="table">
// wrapper and per-cell border/padding/width styles). Those inline styles and
// classes OVERRIDE the website's own CSS, so the content never picks up the
// site design. cleanEditorHtml() strips all of that back down to plain
// semantic markup so the website's CSS styles the content. Runs on load + save
// — no CKEditor model post-fixer (that one froze the editor).

// Presentational attributes we never want to persist. `class`/`style` are
// stripped from EVERY element; the rest are legacy table attributes so we only
// remove them from table elements (removing width/height off an <img> would
// break intended image sizing).
const GLOBAL_JUNK_ATTRS = ['class', 'style'];
const TABLE_JUNK_ATTRS = [
  'width', 'height', 'bgcolor', 'background',
  'border', 'align', 'valign', 'cellpadding', 'cellspacing',
];

// Wrapper tags that only ever carried styling here — unwrap them (keep their
// contents, drop the tag itself).
const UNWRAP_TAGS = new Set(['DIV', 'SPAN', 'FONT', 'SECTION', 'ARTICLE']);

const unwrap = (el) => el.replaceWith(...el.childNodes);

const cleanEditorHtml = (html) => {
  if (!html || html.indexOf('<') === -1) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // 1. Unwrap the <figure class="table"> div CKEditor puts around each table
  //    (leave image figures alone — they carry the caption structure).
  doc.querySelectorAll('figure').forEach((figure) => {
    const table = figure.querySelector(':scope > table');
    if (table) figure.replaceWith(table);
  });

  // 2. Unwrap junk wrapper tags (div/span/font/...). querySelectorAll is in
  //    document order (outer before inner) and unwrapping keeps children in the
  //    document, so nested wrappers are still reached and flattened.
  doc.querySelectorAll('div, span, font, section, article').forEach((el) => {
    if (UNWRAP_TAGS.has(el.tagName)) unwrap(el);
  });

  // 3. Strip `class` and inline `style` from every remaining element.
  doc.querySelectorAll('*').forEach((el) => {
    GLOBAL_JUNK_ATTRS.forEach((attr) => el.removeAttribute(attr));
  });

  // 4. Tables: strip legacy presentational attributes, promote header cells to
  //    <th>, and collapse a double heading row into a single header.
  doc.querySelectorAll('table').forEach((table) => {
    table
      .querySelectorAll('thead, tbody, tfoot, tr, th, td, col, colgroup, caption')
      .forEach((el) => TABLE_JUNK_ATTRS.forEach((a) => el.removeAttribute(a)));
    TABLE_JUNK_ATTRS.forEach((a) => table.removeAttribute(a));

    const thead = table.querySelector(':scope > thead');
    if (!thead) return;

    const headRows = thead.querySelectorAll(':scope > tr');

    // 4a. Word often exports header cells as <td><strong>..</strong></td>.
    //     Promote the first heading row's cells to real <th> so the website can
    //     style headers via CSS.
    if (headRows[0]) {
      headRows[0].querySelectorAll(':scope > td').forEach((td) => {
        const th = doc.createElement('th');
        th.innerHTML = td.innerHTML;
        td.replaceWith(th);
      });
    }

    // 4b. Word / Google Docs tables sometimes paste in with TWO heading rows,
    //     so CKEditor puts two <tr> inside <thead> and the website renders both
    //     as bold headers. Keep only the first <thead> row; move any extra ones
    //     into <tbody> as normal <td> cells.
    if (headRows.length <= 1) return;

    let tbody = table.querySelector(':scope > tbody');
    if (!tbody) {
      tbody = doc.createElement('tbody');
      table.appendChild(tbody);
    }

    const extraRows = Array.from(headRows).slice(1);

    extraRows.forEach((tr) => {
      tr.querySelectorAll('th').forEach((th) => {
        const td = doc.createElement('td');
        td.innerHTML = th.innerHTML;
        th.replaceWith(td);
      });
    });

    const firstBodyRow = tbody.firstChild;
    extraRows.forEach((tr) => tbody.insertBefore(tr, firstBodyRow));
  });

  return doc.body.innerHTML;
};

// Kept as aliases so existing imports keep working.
const cleanTableHtml = cleanEditorHtml;
const limitTableHeader = cleanEditorHtml;

export { ClassicEditor, ckeditorConfig, cleanEditorHtml, cleanTableHtml, limitTableHeader };
export default ckeditorConfig;
